import React, {useState, useEffect, useRef, useMemo} from "react";

import {
  Typography,
  Button,
  Box,
  FormControlLabel,
  RadioGroup,
  Radio,
  Slider,
  Slide,
  Fade,
} from "@mui/material";

import { makeStyles } from "tss-react/mui";

import SpeechBubble from "./SpeechBubble";

import CompletionStep from "./CompletionStep";

import { formatNumber } from 'ui-common/i18n';
import { useTranslation } from 'react-i18next';
import MaskSelectorToolStyles from "./MaskSelectorToolStyles";

import { sendGAEvent } from "ui-common/GoogleAnalytics";

import {
  brightcoveAccountId,
  brightcovePlayerId,
} from "../../AppSettings";
import BrightcovePlayer from "../../BrightcovePlayer";
import next_arrow from "./img/next_arrow.svg";

import { scrollToTop } from "ui-common/components/common";

const MeasurementSlider = ({
  name,
  value,
  unitName,
  unitValue,
  stepSize,
  handleChange,
  setFieldValue,
  unitTypes,
  min,
  max,
}) => {
  return (
    <>
      <RadioGroup name={unitName} value={unitValue} onChange={handleChange} row>
        {unitTypes.map(({ label, value: v }) => (
          <FormControlLabel
            key={v}
            control={<Radio color="secondary" />}
            value={v}
            label={label}
          />
        ))}
      </RadioGroup>
      <Box mt={3}>
        <Slider
          aria-label="sizing slider"
          name={name}
          value={value}
          onChange={(e, value) => setFieldValue(name, value)}
          min={min}
          max={max}
          step={stepSize}
          valueLabelDisplay="on"
          valueLabelFormat={(x) => formatNumber(x, "0.[000]")}
          color="secondary"      
          data-testid={`measurement-slider-${name}`}
        />
      </Box>
    </>
  );
};

const NoseDepthButtons = ({ value, handleChange }) => {

  const { t: tSurvey } = useTranslation("survey_common");
  const lessThanOneInchAnswer = tSurvey("lessThanOneInchAnswer");
  const oneInchOrGreaterAnswer = tSurvey("oneInchOrGreaterAnswer");

  return (
    <RadioGroup name="nose_depth" value={value} onChange={handleChange} row data-testid="nose-depth-buttons">
      <FormControlLabel
        value="20"
        control={<Radio color="secondary" />}
        label={ lessThanOneInchAnswer }
      />
      <FormControlLabel
        value="30"
        control={<Radio color="secondary" />}
        label={ oneInchOrGreaterAnswer }
      />
    </RadioGroup>
  )
};

const sizingStyles = makeStyles()((theme) => ({
  descriptionText: {
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  button: {
    display: "flex",
    marginLeft: theme.spacing(1),
  },
}));

const MaskMeasurement = ({
  name,
  text,
  unitTypes,
  videoUrl,
  brightcoveVideoId,
  max,
  min,
  values,
  setFieldValue,
  handleChange,
  onVideoPlay,
  onVideoEnded,
  firstStep
}) => {
  const value = values[name];
  const unitName = `${name}_unit`;
  const unitValue = values[`${name}_unit`];
  const stepSize = unitValue === "in" ? 0.125 : 0.1;

  const {classes: commonClasses} = MaskSelectorToolStyles();

  const ref = useRef();
  const textRef = useRef(null);

  useEffect(() => { 
    if(textRef.current && !firstStep) {
      textRef.current.focus();
    }

    scrollToTop();

  }, [firstStep]);

  return (
    <Box ref={ref}>
      <SpeechBubble>
        <Typography className={commonClasses.noOutline} variant="body1" tabIndex="0" aria-label={text} ref={textRef}>{text}</Typography>
      </SpeechBubble>
      <Fade in={true} timeout={1000}>
        <Box>
            <Box
              width="100%"
              mt={4}
              mb={2}
              border="4px solid"
              borderColor="grey.400"
              position="relative"
            >
              <BrightcovePlayer
                accountId={brightcoveAccountId}
                playerId={brightcovePlayerId}
                videoId={brightcoveVideoId}
              />
            </Box>
          {name === "nose_depth" ? (
            <NoseDepthButtons value={value} handleChange={handleChange} />
          ) : (
            <MeasurementSlider
              name={name}
              value={value}
              unitName={unitName}
              unitValue={unitValue}
              stepSize={stepSize}
              handleChange={handleChange}
              setFieldValue={setFieldValue}
              unitTypes={unitTypes}
              min={min}
              max={max}
            />
          )}
        </Box>
      </Fade>
    </Box>
  );
};

const MaskSizing = ({
  values,
  setFieldValue,
  handleChange,
  handleSubmit,
  isSubmitting,
  handlePreviousExternal,
  currentStep,
  handleNext,
  handlePrevious,
  currentPageHasTransition
}) => {

  const { t } = useTranslation("pages.MaskSelectorTool.MaskSizing");
  const { t: tSurvey } = useTranslation("survey_common");

  // Localization strings
  const prevButton = t("prevButton");
  const nextButton = t("nextButton");
  const noseWidthQuestion = tSurvey("noseWidthQuestion");

  const faceHeightQuestion = tSurvey("faceHeightQuestion");
  const noseDepthQuestion = tSurvey("noseDepthQuestion");
  const inchesLabel = t("inchesLabel");
  const centimetersLabel = t("centimetersLabel");

  const brightcoveNoseWidthVideoId = t("brightcoveNoseWidthVideoId");
  const brightcoveFaceHeightVideoId = t("brightcoveFaceHeightVideoId");
  const brightcoveNoseDepthVideoId = t("brightcoveNoseDepthVideoId");

  const questions = [
    {
      name: "nose_width",
      text: noseWidthQuestion,
      unitTypes: [
        { label: inchesLabel, value: "in" },
        { label: centimetersLabel, value: "cm" },
      ],
      max: 7,
      min: 0,
      brightcoveVideoId: brightcoveNoseWidthVideoId
    },
    {
      name: "face_height",
      text: faceHeightQuestion,
      unitTypes: [
        { label: inchesLabel, value: "in" },
        { label: centimetersLabel, value: "cm" },
      ],
      max: 12,
      min: 0,
      brightcoveVideoId: brightcoveFaceHeightVideoId
    },
    {
      name: "nose_depth",
      text: noseDepthQuestion,
      brightcoveVideoId: brightcoveNoseDepthVideoId
    },
  ];

  // Constants for video state
  const videoStateUnplayed = "unplayed";
  const videoStateStarted = "started";
  const videoStateFinished = "finished";
  
  const [step, setStep] = useState(0);
  const [videoState, setVideoState] = useState(videoStateUnplayed);

  const states = useMemo(() => {
    return {
      sizing_manual_nose_width: 0,
      sizing_manual_nose_width_prep: 0,
      sizing_manual_nose_width_no_camera: 0,
      sizing_manual_face_height: 1,
      sizing_manual_face_height_prep: 1,
      sizing_manual_face_height_no_camera: 1,
      sizing_manual_nose_depth: 2,
      sizing_manual_nose_depth_prep: 2,
      sizing_manual_nose_depth_no_camera: 2,
      sizing_manual_complete: 2,
      sizing_manual_complete_prep: 2,
      sizing_manual_complete_no_camera: 2
    };
  }, []);

  useEffect(() => {
    setStep(states[currentStep]);
  }, [currentStep, states]);

  const onVideoPlay = () => {
    if(videoState !== videoStateFinished) {
      setVideoState(videoStateStarted);
    }
  };

  const onVideoEnded = () => {
    setVideoState(videoStateFinished);
  }

  const sendVideoAnalytics = (step) => {
    const stepName = questions[step].name;
    
    let videoAction = "";
    
    switch (videoState) {
      case videoStateUnplayed:
        videoAction = "skipped";
        break;
        
      case videoStateStarted:
        videoAction = "partially_viewed";
        break;
          
      case videoStateFinished:
        videoAction = "completed";
        break;
            
      default:
        videoAction = "unknown";
        break;
    }
    
    setVideoState(videoStateUnplayed);
    
    sendGAEvent("Video", videoAction, stepName);
  }

  const handleNextInternal = () => {
    sendVideoAnalytics(step);
    handleNext();
  };

  const handlePreviousInternal = (e) => {
    if (step < questions.length) {
      sendVideoAnalytics(step);
    }

    let previousStep = step - 1;
    setStep( previousStep < 0 ? 0 : previousStep);

    handlePrevious(e);

  };

  const {classes} = sizingStyles();

  return (
    <>
      <Box bgcolor="background.paper" sx={{padding: "24px 0"}}>
        <>
          {questions.map((entry, i) => {
            if (
              step === i ||
              (step === questions.length && i === questions.length - 1)
            ) {
              return (
                <Slide
                  key={entry.name}
                  direction="right"
                  in
                  mountOnEnter
                  unmountOnExit
                >
                  <Box position="relative">
                    <MaskMeasurement
                      values={values}
                      setFieldValue={setFieldValue}
                      handleChange={handleChange}
                      scroll={step !== 0} // let the patient read the title
                      onVideoPlay={onVideoPlay}
                      onVideoEnded={onVideoEnded}
                      firstStep={i === 0}
                      {...entry}
                    />
                  </Box>
                </Slide>
              );
            } else {
              return null;
            }
          })}
          <Box pY={1} display="flex" justifyContent="flex-end">
            
            { step === 0 && handlePreviousExternal !== undefined && (
                <Box mt="20px">
                  <Button
                      disableRipple
                      variant="outlined"
                      color="secondary"
                      onClick={handlePreviousExternal}
                      className={classes.button}
                  >{prevButton}</Button>
                </Box>
            )}
            
            { currentPageHasTransition("PREVIOUS") && (
                <Box mt="20px">
                  <Button
                      sx={{height: '40px'}}
                      disableRipple
                      variant="outlined"
                      color="secondary"
                      onClick={handlePreviousInternal}
                      className={classes.button}
                      data-testid="sizing-back-button"
                  >{prevButton}</Button>
                </Box>
            )}

            { currentPageHasTransition("NEXT") && !currentStep.startsWith("sizing_manual_complete") && (
                <Box mt="20px">
                  <Button
                      disableRipple
                      variant="contained"
                      sx={{padding: '10px 16px 10px 24px', height: '40px'}}
                      color="secondary"
                      onClick={handleNextInternal}
                      disabled={!values[questions[step].name]}
                      className={classes.button}
                      data-testid="sizing-next-button"
                  >{nextButton}
                    <Box padding='3px'></Box>
                    <img alt="" src={next_arrow}/>
                  </Button>
                </Box>
            )}

          </Box>
        </>
      </Box>
      { currentStep.startsWith("sizing_manual_complete") && (
        <CompletionStep
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
};

export default MaskSizing;
